// DocsContent.jsx
import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion, AnimatePresence } from 'framer-motion';
import { loadMarkdownContent } from '../../utils/markdownLoader';
import './DocsContent.scss';

export const DocsContent = ({ section }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayContent, setDisplayContent] = useState({
    content: '',
    metadata: {},
    section: null,
  });

  useEffect(() => {
    let isMounted = true;
    let timeoutId;

    const loadContent = async () => {
      if (!section?.path) return;

      setIsLoading(true);
      setError(null);

      try {
        const { content, metadata } = await loadMarkdownContent(section.path);

        // Only proceed if component is still mounted
        if (!isMounted) return;

        // Clear previous timeout if exists
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        // Set new timeout for content transition
        timeoutId = setTimeout(() => {
          if (isMounted) {
            setDisplayContent({ content, metadata, section });
            setIsLoading(false);
          }
        }, 300);
      } catch (err) {
        if (isMounted) {
          console.error('Error loading content:', err);
          setError(
            'Failed to load documentation content. Please try again later.'
          );
          setIsLoading(false);
        }
      }
    };

    loadContent();

    // Cleanup function
    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [section]);

  return (
    <div className="docs-content-wrapper d-flex">
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="docs-loading d-flex mx-auto"
          >
            <div className="loading-content m-auto d-flex flex-column align-items-center">
              <div className="spinner-ring mx-auto">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="loading-text"
              >
                Loading documentation...
              </motion.div>
            </div>
          </motion.div>
        ) : error ? (
          <motion.div
            key="error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="docs-error alert alert-danger d-flex align-items-center"
          >
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="docs-content"
          >
            <div className="docs-header">
              <motion.h1
                className="docs-title"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                {displayContent.section?.title}
              </motion.h1>
              <motion.div
                className="docs-metadata mb-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                {displayContent.metadata.author && (
                  <div className="metadata-item">
                    <i className="bi bi-person-circle"></i>
                    <span>{displayContent.metadata.author}</span>
                  </div>
                )}
                {displayContent.metadata.lastUpdated && (
                  <div className="metadata-item">
                    <i className="bi bi-clock-history"></i>
                    <span>
                      {new Date(
                        displayContent.metadata.lastUpdated
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {displayContent.metadata.version && (
                  <div className="metadata-item">
                    <i className="bi bi-tag"></i>
                    <span>{displayContent.metadata.version}</span>
                  </div>
                )}
              </motion.div>
            </div>
            <motion.div
              className="markdown-content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  // Add custom components for markdown elements if needed
                  a: ({ node, ...props }) => (
                    <a {...props} target="_blank" rel="noopener noreferrer" />
                  ),
                  code: ({ node, inline, ...props }) => (
                    <code
                      className={`${inline ? 'inline-code' : 'code-block'}`}
                      {...props}
                    />
                  ),
                }}
              >
                {displayContent.content}
              </ReactMarkdown>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
